import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, Search, Users, ChevronRight, Shield, Lock, Plus, Pencil, Trash2, Upload
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddIndividualModal from '@/components/modals/AddIndividualModal';
import { BulkIndividualsDialog } from '@/components/bulk/BulkRecordsDialogs';
import { toast } from 'sonner';

export default function ClientIndividuals() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [individuals, setIndividuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [confidentialityFilter, setConfidentialityFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingIndividual, setEditingIndividual] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchIndividuals = async () => {
    if (!token || !clientId) return;
    try {
      const response = await api.getIndividuals(token, clientId);
      setIndividuals(response.data);
    } catch (error) {
      console.error('Failed to fetch individuals:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchIndividuals();
  }, [token, clientId]);

  const handleAddIndividual = async (data) => {
    try {
      await api.createIndividual(token, clientId, data);
      toast.success('Individual added successfully');
      await fetchIndividuals();
    } catch (error) {
      console.error('Failed to add individual:', error);
      toast.error('Failed to add individual');
      throw error;
    }
  };

  const handleEditIndividual = async (data) => {
    try {
      await api.updateIndividual(token, clientId, editingIndividual.id, data);
      toast.success('Individual updated successfully');
      await fetchIndividuals();
      setEditingIndividual(null);
    } catch (error) {
      console.error('Failed to update individual:', error);
      toast.error('Failed to update individual');
      throw error;
    }
  };

  const handleDeleteIndividual = async (individual) => {
    try {
      await api.deleteIndividual(token, clientId, individual.id);
      toast.success('Individual deleted successfully');
      await fetchIndividuals();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete individual:', error);
      toast.error('Failed to delete individual');
    }
  };

  const filteredIndividuals = individuals.filter(ind => {
    const fullName = `${ind.firstName} ${ind.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) ||
                          ind.role?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesConfidential = confidentialityFilter === 'all' || 
                                (confidentialityFilter === 'confidential' && ind.confidential) ||
                                (confidentialityFilter === 'standard' && !ind.confidential);
    return matchesSearch && matchesConfidential;
  });

  // Group by generation
  const groupedByGeneration = filteredIndividuals.reduce((acc, ind) => {
    const gen = ind.generation || 0;
    if (!acc[gen]) acc[gen] = [];
    acc[gen].push(ind);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="individuals-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Individuals</h1>
          <p className="text-[#5D524D]">{filteredIndividuals.length} people in {client?.name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setShowBulkModal(true)}
            variant="outline"
            data-testid="bulk-individuals-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button 
            onClick={() => setShowAddModal(true)}
            className="caffeine-btn"
            data-testid="add-individual-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Individual
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
          <Input
            placeholder="Search by name or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="caffeine-input pl-10"
            data-testid="individuals-search-input"
          />
        </div>
        <Select value={confidentialityFilter} onValueChange={setConfidentialityFilter}>
          <SelectTrigger className="w-[180px] caffeine-input" data-testid="confidentiality-filter">
            <Shield className="w-4 h-4 mr-2 text-[#8C8078]" />
            <SelectValue placeholder="All Access Levels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Access Levels</SelectItem>
            <SelectItem value="confidential">Confidential Only</SelectItem>
            <SelectItem value="standard">Standard Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Individuals by Generation */}
      {Object.keys(groupedByGeneration).sort((a, b) => a - b).map(gen => (
        <div key={gen} className="mb-8">
          <h2 className="text-sm font-medium text-[#8C8078] uppercase tracking-wider mb-3">
            Generation {gen}
          </h2>
          <div className="caffeine-card overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Family Role</th>
                  <th>Corporate Roles</th>
                  <th>Date of Birth</th>
                  <th>Contact</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {groupedByGeneration[gen].map(ind => (
                  <tr key={ind.id} data-testid={`individual-row-${ind.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-sm font-medium">
                          {ind.firstName?.[0] || ''}{ind.lastName?.[0] || ''}
                        </div>
                        <div>
                          <span className="font-medium text-[#2C2420]">
                            {ind.firstName}{ind.middleName ? ` ${ind.middleName}` : ''} {ind.lastName}
                          </span>
                          {ind.preferredName && (
                            <span className="text-xs text-[#8C8078] ml-2">
                              ({ind.preferredName})
                            </span>
                          )}
                        </div>
                        {ind.confidential && (
                          <Shield className="w-4 h-4 text-[#7D8C9E]" />
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="text-[#4A3B32] font-medium">
                        {ind.familyRole || ind.role || '—'}
                      </span>
                    </td>
                    <td>
                      {(ind.corporateRoles && ind.corporateRoles.length > 0) ? (
                        <div className="flex flex-wrap gap-1">
                          {ind.corporateRoles.slice(0, 2).map((role, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 text-xs rounded-full bg-[#4A3B32]/10 text-[#4A3B32]"
                            >
                              {role}
                            </span>
                          ))}
                          {ind.corporateRoles.length > 2 && (
                            <span className="text-xs text-[#8C8078]">
                              +{ind.corporateRoles.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#8C8078]">—</span>
                      )}
                    </td>
                    <td>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="masked-field cursor-help">
                              <Lock className="w-3.5 h-3.5" />
                              •• ••• ••••
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="sensitive-tooltip">
                            Sensitive fields are permission-controlled and fully audited in the live product.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td>
                      <div className="space-y-1">
                        {ind.email && (
                          <div className="text-sm text-[#5D524D]">{ind.email}</div>
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="masked-field cursor-help text-xs">
                                <Lock className="w-3 h-3" />
                                +61 ••• ••• •••
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="sensitive-tooltip">
                              Sensitive fields are permission-controlled and fully audited in the live product.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setEditingIndividual(ind)}
                          className="caffeine-btn-ghost p-2"
                          data-testid={`edit-individual-${ind.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setDeleteConfirm(ind)}
                          className="caffeine-btn-ghost p-2 text-red-600 hover:text-red-700"
                          data-testid={`delete-individual-${ind.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/app/clients/${clientId}/individuals/${ind.id}`)}
                          className="caffeine-btn-ghost"
                          data-testid={`view-individual-${ind.id}`}
                        >
                          View
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Empty State */}
      {filteredIndividuals.length === 0 && (
        <div className="empty-state">
          <Users className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No individuals found</p>
          <p className="text-sm">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Add Individual Modal */}
      <AddIndividualModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddIndividual}
        clientId={clientId}
      />

      {/* Bulk Individuals Import */}
      <BulkIndividualsDialog
        open={showBulkModal}
        onOpenChange={setShowBulkModal}
        clientId={clientId}
        onImported={fetchIndividuals}
      />

      {/* Edit Individual Modal */}
      <AddIndividualModal
        isOpen={!!editingIndividual}
        onClose={() => setEditingIndividual(null)}
        onSave={handleEditIndividual}
        clientId={clientId}
        initialData={editingIndividual}
        isEdit={true}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Individual</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteConfirm?.firstName} {deleteConfirm?.lastName}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteIndividual(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
